import logger from '@/utils/logger';
/**
 * MODAL DE PAIEMENT INTÉGRÉ
 * Utilise Stripe Payment Element pour un paiement sans redirection
 */

import React, { useState, useEffect } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { X, CreditCard, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';

// Charger Stripe avec la clé publique
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  planName: string;
  planPrice: number;
  planSlug: string;
  planTokens: number;
  onSuccess: () => void;
}

// Formulaire de paiement (à l'intérieur du provider Elements)
function PaymentForm({ planName, planPrice, planSlug, onSuccess, onClose }: Omit<PaymentModalProps, 'isOpen'>) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Confirmer le SetupIntent
      const { error: submitError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: `${window.location.origin}/billing/success`,
        },
      });

      if (submitError) {
        setError(submitError.message || 'Erreur lors du paiement');
        setLoading(false);
        return;
      }

      if (!setupIntent || setupIntent.status !== 'succeeded') {
        setError('La confirmation du paiement a échoué');
        setLoading(false);
        return;
      }

      // Appeler le backend pour créer l'abonnement
      // Utiliser Supabase pour récupérer le token (comme dans createPaymentIntent)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }
      
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      
      const response = await fetch(`${apiUrl}/api/billing/confirm-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          setup_intent_id: setupIntent.id,
          plan_slug: planSlug,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Erreur création abonnement');
      }

      // Abonnement créé !
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Erreur inattendue');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
        <h3 className="text-2xl font-bold text-theme-primary mb-2">Paiement réussi !</h3>
        <p className="text-theme-secondary">Votre abonnement est maintenant actif.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col h-full max-h-[calc(100vh-200px)]">
      {/* Contenu scrollable */}
      <div className="flex-1 overflow-y-auto space-y-6 pr-2">
        {/* Informations du plan */}
        <div className="p-4 rounded-xl bg-theme-hover/50 border border-theme-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-theme-secondary">Plan sélectionné</span>
            <span className="text-theme-primary font-semibold">{planName}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-theme-secondary">Montant</span>
            <span className="text-2xl font-bold text-theme-primary">{planPrice}€<span className="text-sm text-theme-secondary">/mois</span></span>
          </div>
        </div>

        {/* Payment Element de Stripe */}
        <div className="p-4 rounded-xl bg-theme-surface border border-theme-border">
          <PaymentElement 
            options={{
              layout: 'tabs',
            }}
          />
        </div>

        {/* Erreur */}
        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Actions - Toujours visibles en bas */}
      <div className="flex-shrink-0 pt-6 border-t border-theme-border mt-6">
        <div className="flex gap-3">
          <Button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 py-3 bg-theme-hover hover:bg-neutral-700 text-theme-primary rounded-xl font-semibold transition-colors"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={!stripe || loading}
            className="flex-1 py-3 bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 dark:from-purple-500 dark:via-indigo-500 dark:to-blue-500 hover:scale-105 text-white rounded-xl font-semibold transition-all shadow-lg shadow-purple-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                Paiement en cours...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Payer {planPrice}€
              </span>
            )}
          </Button>
        </div>

        {/* Sécurité */}
        <div className="text-center text-xs text-theme-tertiary mt-4">
          Paiement sécurisé par Stripe. Vos informations bancaires ne sont jamais stockées sur nos serveurs.
        </div>
      </div>
    </form>
  );
}

// Modal principale avec le provider Stripe Elements
export default function PaymentModal({
  isOpen,
  onClose,
  planName,
  planPrice,
  planSlug,
  planTokens,
  onSuccess,
}: PaymentModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      createPaymentIntent();
    }
  }, [isOpen, planSlug]);

  const createPaymentIntent = async () => {
    setLoading(true);
    setError(null);

    try {
      // Récupérer le token d'authentification depuis Supabase
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Session expirée. Veuillez vous reconnecter.');
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';

      // Créer un SetupIntent pour l'abonnement (nécessite une carte enregistrée)
      const response = await fetch(`${apiUrl}/api/billing/create-subscription-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ plan_slug: planSlug }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('[PaymentModal] Response error:', errorText);
        
        let errorMessage = 'Erreur lors de la création du paiement';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        
        throw new Error(errorMessage);
      }

      const { clientSecret } = await response.json();
      setClientSecret(clientSecret);
    } catch (err: any) {
      logger.error('[PaymentModal] Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-theme-base/80 backdrop-blur-sm">
      <div className="w-full max-w-2xl max-h-[90vh] bg-theme-surface rounded-2xl border border-theme-border shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-theme-border">
          <div>
            <h2 className="text-2xl font-bold text-theme-primary">Paiement sécurisé</h2>
            <p className="text-sm text-theme-secondary mt-1">Abonnement {planName}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-theme-hover rounded-xl transition-colors"
          >
            <X className="w-6 h-6 text-theme-secondary" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col" style={{ minHeight: 0 }}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="w-12 h-12 text-purple-600 dark:text-purple-400 animate-spin mb-4" />
              <p className="text-theme-secondary">Préparation du paiement...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                <X className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-red-400 mb-4">{error}</p>
              <Button
                onClick={createPaymentIntent}
                className="px-6 py-2 bg-purple-600 dark:bg-purple-500 hover:bg-purple-700 dark:hover:bg-purple-600 text-white rounded-xl transition-colors"
              >
                Réessayer
              </Button>
            </div>
          ) : clientSecret ? (
            <Elements 
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: 'night',
                  variables: {
                    colorPrimary: '#f97316',
                    colorBackground: '#171717',
                    colorText: '#ffffff',
                    colorDanger: '#ef4444',
                    borderRadius: '12px',
                  },
                },
              }}
            >
              <PaymentForm
                planName={planName}
                planPrice={planPrice}
                planSlug={planSlug}
                planTokens={planTokens}
                onSuccess={onSuccess}
                onClose={onClose}
              />
            </Elements>
          ) : null}
        </div>
      </div>
    </div>
  );
}
